/**
 * ThreadView — one roster entry's message history as a chat surface, hosted
 * in-page in place of the inbox header/filters/table (Section D re-skin of
 * 2.3's ThreadDrawer, now retired). Reuses 2.3's useOutreachThread /
 * useOutreachThreadActions and Section C's InboundMessageCard verbatim —
 * only the presentation moved from a right-side sheet into bubbles.
 *
 * useOutreachThread ships page 1 / limit 50 only (no pagination parameter) —
 * so unlike the plan text's sketch, "Load earlier" isn't wired to a real
 * fetch here. This shows the same "first N of total" notice ThreadDrawer
 * used instead of a button that would silently do nothing.
 *
 * The removed-creator check reads `outreachState === 'removed'`: the one model for "removed", whether the
 * roster's removed_by_admin or the thread's own removal put it there.
 *
 * `actionError` carries ThreadDrawer's own recovery-action error surfacing
 * forward unchanged: a failed resend/mark-sent/cancel shows here as a
 * role="alert" instead of disappearing silently.
 *
 * Task 24: the placeholder composer is replaced by the live ThreadComposer;
 * every rate action lives in its pill row, never on a message card, and a
 * seed only fills it (it never sends); opening a thread whose row is
 * unread calls onMarkRead once (ruling 50, guarded by entryId so
 * prev/next — which reuses this same mounted component — re-arms per
 * entry); the entry-action callbacks (mark unread, pause/resume follow-ups,
 * close/reopen, send-to-brand, remove) are lifted to the caller so the
 * thread ⋯ menu (ThreadHeader) reuses the exact same handlers/toast copy
 * RowActions' row menu already does, rather than a second implementation.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useOutreachEntryEvents, useOutreachPool, useOutreachThread, useOutreachThreadActions } from '@discovr/features';
import type { OutreachCloseReason, OutreachContactPatch, OutreachMessage, OutreachQuote, OutreachRate, OutreachRow } from '@discovr/types';
import { UNSPECIFIED_DELIVERABLE } from '@discovr/types';
import { CounterRatesDialog } from './CounterRatesDialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { cn } from '../../../lib/utils';
import { InboundMessageCard } from './InboundMessageCard';
import { matchConfidenceHelp } from './matchConfidence';
import { ThreadMessageCard, isHiddenCancelled, isNotSent, isScheduledAhead, sendAnywayOverride } from './ThreadMessageCard';
import { ThreadBubble, fmtThreadTime } from './ThreadBubble';
import { MessageTag } from './MessageTag';
import { IntentPill } from './IntentPill';
import { ThreadHeader } from './ThreadHeader';
import { ThreadComposer, type ComposerDraft } from './ThreadComposer';
import { useStickToBottom } from './useStickToBottom';
import { canAgreeRate, isApprovedWaiting, isFirstTouchDraft } from './handoffStates';
import { waitingSendHint } from './OutreachRowPills';
import { BrandStrip } from './BrandStrip';
import { RecordBrandCounterDialog } from './RecordBrandCounterDialog';
import { CreatorProfileDrawer } from './CreatorProfileDrawer';
import { recoveryErrorCopy } from './sendLabels';
import { mergeThreadItems, timelineActorTitle, timelineLabel } from './threadTimeline';
import { AWAITING_REPLY_HINT, AWAITING_REPLY_LABEL, escalationClearedLabel, escalationLabel, escalationTitle, lostJobKind } from './rowStateLabels';

export interface ThreadViewProps {
  campaignId: string;
  row: OutreachRow;
  /** The inbox's current page, for prev/next and for the row lookup that
   *  drives which row this is (see index of `row.entryId` within it). */
  pageRows: OutreachRow[];
  canWrite: boolean;
  /** Send anyway on a risky address is admin tier only (outreach:manage, owner ruling 2026-10-12). */
  canOverrideAddressGuard?: boolean;
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch (default ON). */
  autoFollowups?: boolean;
  onBack: () => void;
  onNavigate: (entryId: string) => void;
  onSaveContact: (patch: OutreachContactPatch) => Promise<void>;
  onMarkRead: (entryId: string) => void;
  onMarkUnread: (entryId: string) => void;
  onPauseFollowups: (entryId: string) => void;
  onResumeFollowups: (entryId: string) => void;
  onClose: (entryId: string, reason: OutreachCloseReason) => void;
  onReopen: (entryId: string) => void;
  onNotUnsubscribe: (entryId: string) => void;
  onSendToBrand: (entryId: string) => void;
  onRemove: (entryIds: string[]) => void;
  onAgreeRate: (entryId: string, quote?: OutreachQuote) => void;
  onClearEscalation?: (entryId: string) => void;
  onRecordBrandCounter: (entryId: string, note?: string) => void;
  onClearBrandCounter?: (entryId: string) => void;
  onNoReplyNeeded?: (entryId: string) => void;
  onSendLapsedFollowup?: (entryId: string) => void;
  onRetryLookup?: (entryId: string) => void;
  onSkipLapsedFollowup?: (entryId: string) => void;
  onOpenRosterChat: (row: OutreachRow) => void;
  composerSeed?: ComposerDraft;
  onSeedConsumed?: () => void;
}

export function ThreadView({
  campaignId,
  row,
  pageRows,
  canWrite,
  canOverrideAddressGuard = false,
  campaignZone,
  autoFollowups,
  onBack,
  onNavigate,
  onSaveContact,
  onMarkRead,
  onMarkUnread,
  onPauseFollowups,
  onResumeFollowups,
  onClose,
  onReopen,
  onNotUnsubscribe,
  onSendToBrand,
  onRemove,
  onAgreeRate,
  onClearEscalation,
  onRecordBrandCounter,
  onClearBrandCounter,
  onNoReplyNeeded,
  onSendLapsedFollowup,
  onRetryLookup,
  onSkipLapsedFollowup,
  onOpenRosterChat,
  composerSeed,
  onSeedConsumed,
}: ThreadViewProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const thread = useOutreachThread(row.entryId);
  const events = useOutreachEntryEvents(row.entryId);
  const actions = useOutreachThreadActions(campaignId, row.entryId);
  const pool = useOutreachPool(campaignId, true);
  const [counterOpen, setCounterOpen] = useState(false);
  const [attachFor, setAttachFor] = useState<OutreachMessage | null>(null);

  const mailboxName = (id?: number) => pool.data?.find((mailbox) => mailbox.id === id)?.displayName ?? pool.data?.find((mailbox) => mailbox.id === id)?.address;

  const rows = thread.data?.rows ?? [];
  const [showCancelled, setShowCancelled] = useState(false);
  const cancelledToggleId = useId();
  const hiddenCount = rows.filter(isHiddenCancelled).length;
  const firstTouchSent = rows.some((m) => m.direction === 'out' && m.kind === 'initial' && m.status === 'sent');
  const visibleRows = showCancelled ? rows : rows.filter((m) => !isHiddenCancelled(m));
  const items = mergeThreadItems(visibleRows, events.data ?? [], messageTime);
  const total = thread.data?.total ?? 0;
  const idx = pageRows.findIndex((r) => r.entryId === row.entryId);
  const prevRow = idx > 0 ? pageRows[idx - 1] : undefined;
  const nextRow = idx >= 0 && idx < pageRows.length - 1 ? pageRows[idx + 1] : undefined;
  const subject = [...rows].reverse().find((m) => m.direction === 'out' && m.subject)?.subject;
  const hasSent = rows.some((m) => m.direction === 'in' || (m.direction === 'out' && m.status === 'sent'));

  // Every quote reads the same; only the newest rate mail is actionable (through the composer).
  const latestQuoteMessage = [...rows].reverse().find((m) => m.direction === 'in' && carriesQuote(m));
  const latestQuoteMessageId = latestQuoteMessage?.id;
  const latestUnmatched = latestQuoteMessage?.parsed?.unmatched ?? [];
  // The agree prefill and the unmatched gate read the same mail; a reply parsed without a stored quote still offers its rates.
  const latestQuote: OutreachQuote | undefined =
    latestQuoteMessage?.quote ?? (latestQuoteMessage?.parsed?.rates.length ? quoteWithRates(latestQuoteMessage, latestQuoteMessage.parsed.rates) : undefined);
  // Confirm rate on a bundle opens with the bundle's listed deliverables as empty rows (E9-159, R17-21).
  const agreeQuote = bundleAgreeQuote(latestQuoteMessage, latestQuote) ?? latestQuote;
  // Go Task 13: null while a live escalation stands (blanked on re-flag); no
  // zone argument — the thread has no campaign zone in scope, so this reads
  // in the viewer's zone like the bubbles' own timestamps.
  const clearedLine = escalationClearedLabel(row);
  const replyOnly = row.awaitingReply && !row.escalatedAt;

  // Ruling 50: opening a thread marks it read, once per entry. Re-armed on
  // navigating to a different entryId (prev/next reuses this same mounted
  // component rather than remounting it).
  const markedReadFor = useRef<string | null>(null);
  useEffect(() => {
    if (row.unread && markedReadFor.current !== row.entryId) {
      markedReadFor.current = row.entryId;
      onMarkRead(row.entryId);
    }
  }, [row.entryId, row.unread, onMarkRead]);

  // An empty seed only clears the composer (o-e2e2 N6): once applied it is spent, so it can't block a later draft restore.
  useEffect(() => {
    if (composerSeed && composerSeed.body === '') onSeedConsumed?.();
  }, [composerSeed, onSeedConsumed]);

  // A new thread clears dialogs left open on the previous one, adjusted during render (reset-on-prop-change).
  const [prevEntryId, setPrevEntryId] = useState(row.entryId);
  if (row.entryId !== prevEntryId) {
    setPrevEntryId(row.entryId);
    setCounterOpen(false);
    setAttachFor(null);
  }

  // Only the message that started an action goes busy — a thread-wide flag
  // would freeze every other message's controls too (matches 2.3's ThreadDrawer).
  const busyMessageId = actions.resend.isPending
    ? actions.resend.variables
    : actions.sendAnyway.isPending
      ? actions.sendAnyway.variables?.messageId
      : actions.cancel.isPending
        ? actions.cancel.variables
        : actions.markSent.isPending
          ? actions.markSent.variables?.messageId
          : undefined;

  const markSentTargetId = actions.markSent.variables?.messageId;
  const markSentFailedId = actions.markSent.isError ? markSentTargetId : undefined;

  // Carried over from the retired ThreadDrawer: any of the three recovery
  // actions failing surfaces here instead of disappearing silently.
  const actionError = actions.resend.error ?? actions.sendAnyway.error ?? actions.markSent.error ?? actions.cancel.error;

  // The `ours` avatar has no image source today — the pinned/pool mailbox
  // carries no picture — so this label only ever drives its initials
  // fallback, same string the meta line already shows for "from".
  const mailboxLabelFor = (m: OutreachMessage) => mailboxName(m.mailboxId ?? m.requestedMailboxId) ?? 'mailbox pending';

  // Figma's Address slot: the sending mailbox's address for ours (no display name — Figma shows one address), the sender's for theirs.
  const addressLabel = (m: OutreachMessage) => {
    if (m.direction === 'in') return m.fromAddress ?? 'Unknown sender';
    const id = m.mailboxId ?? m.requestedMailboxId;
    const address = m.fromAddress ?? pool.data?.find((mailbox) => mailbox.id === id)?.address;
    return `via ${address ?? 'mailbox pending'}`;
  };
  const timestampLabel = (m: OutreachMessage) => fmtThreadTime(messageTime(m));
  // The email's own Date, on hover, when it reads differently from the arrival shown.
  const timestampTitle = (m: OutreachMessage) => {
    if (m.direction !== 'in' || !m.receivedAt) return undefined;
    const dated = fmtThreadTime(m.receivedAt);
    return dated === timestampLabel(m) ? undefined : `Email dated ${dated}`;
  };

  const isWaitingDraft = (m: OutreachMessage) => isFirstTouchDraft(m.kind, m.status) && !!row.waitingSend && isApprovedWaiting(row, new Date());
  const outboundTagProps = (m: OutreachMessage) =>
    row.waitingSend && isWaitingDraft(m) ? { approvedWaiting: true, hint: waitingSendHint(row.waitingSend, campaignZone, new Date()) } : { hint: m.lastError };

  // Same avatar source the inbox table (OutreachRowsTable → CreatorCell) and
  // ThreadHeader already use for this row — nothing new fetched.
  const creatorAvatarLabel = row.displayName || row.username;

  // The shell above is bounded while a thread is open (AppLayout contentFill), so this flex chain alone
  // sizes the view: header and composer stay put and the message list is the only scroller.
  const { ref: messageListRef, onScroll: onMessageListScroll } = useStickToBottom<HTMLDivElement>(`${rows.length}:${rows.at(-1)?.id ?? ''}`);

  return (
    <div data-testid="thread-view" className="flex min-h-0 flex-1 flex-col">
      <ThreadHeader
        row={row}
        subject={subject}
        hasSent={hasSent}
        campaignZone={campaignZone}
        autoFollowups={autoFollowups}
        canWrite={canWrite}
        canPrev={!!prevRow}
        canNext={!!nextRow}
        onPrev={() => prevRow && onNavigate(prevRow.entryId)}
        onNext={() => nextRow && onNavigate(nextRow.entryId)}
        onBack={onBack}
        onOpenProfile={() => setProfileOpen((v) => !v)}
        profileOpen={profileOpen}
        onMarkUnread={() => onMarkUnread(row.entryId)}
        onPauseFollowups={() => onPauseFollowups(row.entryId)}
        onResumeFollowups={() => onResumeFollowups(row.entryId)}
        onClose={(reason) => onClose(row.entryId, reason)}
        onReopen={() => onReopen(row.entryId)}
        onNotUnsubscribe={() => onNotUnsubscribe(row.entryId)}
        onSendToBrand={() => onSendToBrand(row.entryId)}
        onRemove={() => onRemove([row.entryId])}
        onAgreeRate={() => onAgreeRate(row.entryId, agreeQuote)}
        onClearEscalation={onClearEscalation ? () => onClearEscalation(row.entryId) : undefined}
      />

      {/* Figma 505:50187: a stone-50 content area holding the thread as one centred card, max-width so it still fits narrow screens. */}
      <div data-testid="thread-content" className="flex min-h-0 flex-1 bg-stone-50">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center p-4 md:px-8">
      <div data-testid="thread-card" className="flex min-h-0 w-full max-w-[866px] flex-1 flex-col gap-2.5 rounded-2xl border border-stone-200 bg-card p-4">
      <BrandStrip
        key={`brand-${row.entryId}`}
        row={row}
        canWrite={canWrite}
        onOpenRosterChat={() => onOpenRosterChat(row)}
        onRecordCounter={() => setCounterOpen(true)}
        onClearCounter={onClearBrandCounter ? () => onClearBrandCounter(row.entryId) : undefined}
      />

      {row.outreachState === 'removed' && rows.some((m) => m.direction === 'in') && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-body-sm-regular text-amber-800">
          Reply from a removed creator — the entry stays removed.
        </div>
      )}
      {row.needsYou && (
        <div role="status" title={replyOnly ? AWAITING_REPLY_HINT : escalationTitle(row)} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-body-sm-regular text-rose-800">
          <span>{replyOnly ? AWAITING_REPLY_LABEL : `Needs you · ${escalationLabel(row.escalationReason)}${row.escalationNote && !lostJobKind(row) ? ` — ${row.escalationNote}` : ''}`}</span>
          {canWrite && row.escalatedAt && onClearEscalation ? (
            <Button size="sm" variant="outline" onClick={() => onClearEscalation(row.entryId)}>Clear escalation</Button>
          ) : null}
          {canWrite && row.escalationReason === 'followup_due_while_paused' && onSendLapsedFollowup && onSkipLapsedFollowup ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onSendLapsedFollowup(row.entryId)}>Send follow-up now</Button>
              <Button size="sm" variant="outline" onClick={() => onSkipLapsedFollowup(row.entryId)}>Skip this follow-up</Button>
            </div>
          ) : null}
          {canWrite && lostJobKind(row) === 'find_contacts' && onRetryLookup ? (
            <Button size="sm" variant="outline" onClick={() => onRetryLookup(row.entryId)}>Retry</Button>
          ) : null}
          {canWrite && replyOnly && onNoReplyNeeded ? (
            <Button size="sm" variant="outline" onClick={() => onNoReplyNeeded(row.entryId)}>No reply needed</Button>
          ) : null}
        </div>
      )}
      {clearedLine && (
        <p data-escalation-cleared className="px-1 text-caption-regular text-muted-foreground">{clearedLine}</p>
      )}

      {/* min-h-32, not 0: on a viewport too short for header + composer the page's content column scrolls instead of the thread vanishing. */}
      <div
        ref={messageListRef}
        onScroll={onMessageListScroll}
        role="region"
        aria-label="Conversation"
        tabIndex={0}
        data-testid="thread-messages"
        className="flex min-h-32 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-inset"
      >
        {hiddenCount > 0 && (
          <div className="flex items-center justify-end gap-2">
            <Switch id={cancelledToggleId} checked={showCancelled} onCheckedChange={setShowCancelled} />
            <Label htmlFor={cancelledToggleId} className="text-caption-regular text-muted-foreground">
              {showCancelled ? 'Hide' : 'Show'} cancelled ({hiddenCount})
            </Label>
          </div>
        )}
        {total > rows.length && <p className="text-center text-caption-regular text-muted-foreground">Only part of this thread is loaded.</p>}
        {thread.isLoading && <p className="text-center text-body-sm-regular text-muted-foreground">Loading thread…</p>}
        {thread.isError && (
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-body-sm-regular text-rose-500">Couldn&apos;t load this thread.</p>
            <Button variant="outline" size="sm" onClick={() => void thread.refetch()}>Try again</Button>
          </div>
        )}
        {!thread.isLoading && !thread.isError && visibleRows.length === 0 && <p className="text-center text-body-sm-regular text-muted-foreground">No messages yet.</p>}
        {/* Only the bubbles are the live log, so status text, the retry button and the alert below are not announced as log entries. */}
        <div role="log" aria-label={`Messages with ${creatorAvatarLabel}`} className="flex flex-col gap-6">
        {items.map((item) => {
          if (item.type === 'event') {
            return (
              <p key={`event-${item.event.id}`} data-testid="thread-event" title={timelineActorTitle(item.event)} className="text-center text-caption-regular text-muted-foreground">
                {timelineLabel(item.event)} · {fmtThreadTime(item.event.at)}
              </p>
            );
          }
          const m = item.message;
          return m.direction === 'in' ? (
            <ThreadBubble
              key={m.id}
              side="theirs"
              tag={<MessageTag status={m.status} hint={matchConfidenceHelp(m.matchConfidence)} announceHint />}
              author={creatorAvatarLabel}
              address={addressLabel(m)}
              extra={<IntentPill intent={m.intent} size="tag" />}
              timestamp={timestampLabel(m)}
              timestampTitle={timestampTitle(m)}
              avatarUrl={row.avatarUrl}
              avatarLabel={creatorAvatarLabel}
            >
              <InboundMessageCard
                message={m}
                entryId={row.entryId}
                canWrite={canWrite}
                inBubble
                onAttachUnmatched={canAgreeRate(row) && m.id === latestQuoteMessageId ? () => setAttachFor(m) : undefined}
              />
            </ThreadBubble>
          ) : (
            <ThreadBubble
              key={m.id}
              side="ours"
              tag={<MessageTag status={m.status} notSent={isNotSent(m)} {...outboundTagProps(m)} />}
              author="You"
              address={addressLabel(m)}
              timestamp={timestampLabel(m)}
              avatarLabel={mailboxLabelFor(m)}
            >
              <ThreadMessageCard
                message={m}
                mailboxLabel={mailboxName(m.mailboxId)}
                canWrite={canWrite}
                canOverrideAddressGuard={canOverrideAddressGuard}
                busy={busyMessageId === m.id}
                markSentPending={actions.markSent.isPending && markSentTargetId === m.id}
                markSentFailed={markSentFailedId === m.id}
                inBubble
                approvedWaiting={isWaitingDraft(m)}
                firstTouchSent={firstTouchSent}
                onResend={() => actions.resend.mutate(m.id)}
                onSendAnyway={() => {
                  const override = sendAnywayOverride(m);
                  if (override === 'cooldown') actions.sendAnyway.mutate({ messageId: m.id, override });
                  else if (override === 'address_guard' && m.toAddress) actions.sendAnyway.mutate({ messageId: m.id, override, expectedTo: m.toAddress });
                }}
                onMarkSent={(reason) => actions.markSent.mutate({ messageId: m.id, reason })}
                onCancel={() => actions.cancel.mutate(m.id)}
              />
            </ThreadBubble>
          );
        })}
        </div>
        {actionError && (
          <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-caption-regular text-rose-600">
            {recoveryErrorCopy(actionError)}
          </p>
        )}
      </div>

      <ThreadComposer
        key={`composer-${row.entryId}`}
        campaignId={campaignId}
        entryId={row.entryId}
        row={row}
        canWrite={canWrite}
        latestQuote={latestQuote}
        messages={rows}
        onAgreeRate={canAgreeRate(row) && latestQuoteMessage ? () => onAgreeRate(row.entryId, agreeQuote) : undefined}
        agreeBlockedReason={latestUnmatched.length > 0 ? 'Attach the unmatched amount first' : undefined}
        onAddEmail={() => setProfileOpen(true)}
        initialDraft={composerSeed}
        onSent={() => onSeedConsumed?.()}
        campaignZone={campaignZone}
      />
      </div>
      </div>

      <RecordBrandCounterDialog
        key={`counter-${row.entryId}`}
        open={counterOpen}
        name={row.displayName || row.username}
        onSubmit={(note) => {
          onRecordBrandCounter(row.entryId, note);
          setCounterOpen(false);
        }}
        onCancel={() => setCounterOpen(false)}
      />

      <CounterRatesDialog
        open={attachFor !== null}
        platform={row.platform}
        initial={attachInitial(attachFor)}
        title="Attach to a deliverable"
        description="Pick what each amount is for. Next you agree the rate; nothing is sent."
        submitLabel="Use these rates"
        onSubmit={(rates) => {
          const message = attachFor;
          setAttachFor(null);
          if (message) onAgreeRate(row.entryId, quoteWithRates(message, rates));
        }}
        onCancel={() => setAttachFor(null)}
      />

      <div className={cn('flex min-h-0', profileOpen && 'w-[424px] max-w-[50%] min-w-0 shrink-0 py-4 pr-4')}>
      <CreatorProfileDrawer
        open={profileOpen}
        row={row}
        campaignZone={campaignZone}
        autoFollowups={autoFollowups}
        canWrite={canWrite}
        onSave={onSaveContact}
        onClose={() => setProfileOpen(false)}
        onAgreeRate={() => onAgreeRate(row.entryId)}
        onSendToBrand={() => onSendToBrand(row.entryId)}
        onPauseFollowups={() => onPauseFollowups(row.entryId)}
        onResumeFollowups={() => onResumeFollowups(row.entryId)}
      />
      </div>
      </div>
    </div>
  );
}

/** When a message sits on the timeline: for mail in, when it arrived (not the email's own Date, which a late or
 *  backdated delivery can leave out of order, E9-161); for ours, sent, the time it is scheduled for, else created. */
function messageTime(m: OutreachMessage): string | undefined {
  return m.direction === 'in' ? (m.createdAt ?? m.receivedAt) : (m.sentAt ?? (isScheduledAhead(m) ? m.scheduledFor : m.createdAt));
}

/** A reply the QuoteBlock renders rates for: a stored quote, or parsed rates/unmatched amounts. An auto-reply or a delivery-failure notice is nobody's quote. */
function carriesQuote(m: OutreachMessage): boolean {
  if (m.isAutoReply || m.isDsn) return false;
  return !!m.quote || !!m.parsed?.bundleDetail || (m.parsed?.rates.length ?? 0) > 0 || (m.parsed?.unmatched.length ?? 0) > 0;
}

/** The reply's matched rates followed by each amount the assistant could not place, left for a person to attach. */
function attachInitial(message: OutreachMessage | null): OutreachRate[] | undefined {
  if (!message?.parsed) return undefined;
  // A rate for something that was never pitched must not be agreed by default.
  const matched = message.parsed.rates.filter((r) => !r.notPitched).map(({ deliverable, amount, currency }) => ({ deliverable, amount, currency }));
  const unmatched = message.parsed.unmatched.map((u) => ({ deliverable: UNSPECIFIED_DELIVERABLE, amount: u.amount, currency: u.currency }));
  return [...matched, ...unmatched];
}

/** A bundle as the quote the Agree rate dialog prefills from: each listed deliverable, no amount, plus the package total. */
function bundleAgreeQuote(message: OutreachMessage | undefined, latest: OutreachQuote | undefined): OutreachQuote | undefined {
  const bundle = message?.parsed?.bundleDetail;
  if (!message || !bundle || bundle.deliverableIds.length === 0) return undefined;
  const base = latest ?? { id: 0, round: 0, createdAt: message.createdAt };
  return {
    ...base,
    rates: bundle.deliverableIds.map((deliverable) => ({ deliverable, amount: 0, currency: bundle.currency, confidence: 1, source: 'bundle' })),
    bundle: { amount: bundle.amount, currency: bundle.currency, perUnit: bundle.perUnit, items: bundle.items, freeIds: bundle.freeIds },
  };
}

/** The attached rates as the quote the Agree rate dialog prefills from. */
function quoteWithRates(message: OutreachMessage, rates: OutreachRate[]): OutreachQuote {
  const base = message.quote ?? { id: 0, round: 0, createdAt: message.createdAt };
  return { ...base, rates: rates.map((r) => ({ ...r, confidence: 1, source: 'manual' })) };
}
